// Staff sign-in at a station (plan §10.2–§10.4). The PIN is checked on the device against
// the synced scrypt hash (@enp/domain `verifyPin`), so it works offline. Wrong PINs lock the
// **device** for a while (domain lockout: 5 tries, then 30 s doubling to 15 min; D-047); the
// state lives in `device_state` so a restart doesn't reset it. Permissions come from the
// synced roles for this branch (`hasPermission`), shaping what the station offers; the server
// checks again when the command arrives. A grant counts only while its role exists (archived
// roles drop out of the sync, review CR3-11).
//
// A manager can also unlock the station from the Admin (Settings → Staff, B-080): the
// device rows of this station sync `pin_unlock_requested_at`, and a newer instant than the
// one last applied clears the lock once (a later lock is untouched).
//
// A manager can also approve one action without signing in (D-048): their PIN inside the
// dialog, checked the same way (and counting toward the same lock), plus the permission the
// action needs. The signed-in person stays signed in.

import {
  DEFAULT_PIN_LOCKOUT_POLICY,
  INITIAL_PIN_LOCKOUT,
  type PermissionGrant,
  type PermissionKey,
  type PinLockoutPolicy,
  type PinLockoutState,
  type PinLockoutStatus,
  grantsFromAssignments,
  hasPermission,
  isPermissionKey,
  pinLockoutStatus,
  recordPinFailure,
  recordPinSuccess,
  verifyPin,
} from '@enp/domain';
import { DEVICE_STATE_KEYS, readDeviceState, writeDeviceState } from './device-state.js';
import type { SyncEngine } from './engine.js';
import { Mutex } from './util.js';

export interface StaffMember {
  id: string;
  displayName: string;
}

export interface StaffSession {
  staff: StaffMember;
  branchId: string;
  grants: PermissionGrant[];
  signedInAt: string;
}

/** Why a PIN wasn't accepted (sign-in and manager approval alike). */
export type PinFailure =
  | { ok: false; reason: 'locked'; retryAt: string }
  | {
      ok: false;
      reason: 'wrong_pin';
      attemptsLeft: number;
      lockedUntil: string | null;
      /** How long the station locks when the tries run out (the exact wait, D-047). */
      nextLockMs: number;
    }
  /** Not a staff member of this branch (or archived, or inactive). */
  | { ok: false; reason: 'unknown_staff' }
  /** The stored hash can't be used (no PIN set, or corrupt): a manager must reset it online. */
  | { ok: false; reason: 'pin_unusable' };

export type SignInResult = { ok: true; session: StaffSession } | PinFailure;

export type ApprovalResult =
  | { ok: true; approver: StaffMember; approvedAt: string }
  | PinFailure
  /** The PIN is right, but this person may not approve this. */
  | { ok: false; reason: 'not_permitted' };

export interface StaffSignInOptions {
  engine: SyncEngine;
  now?: () => Date;
  policy?: PinLockoutPolicy;
}

// The people who may work at a branch: active, with a grant (of a role that still exists)
// here or everywhere.
const STAFF_AT_BRANCH_SQL = `SELECT DISTINCT s.id, s.display_name FROM staff s
  JOIN staff_role sr ON sr.staff_id = s.id
  JOIN role r ON r.id = sr.role_id
  WHERE s.active = 1 AND (sr.branch_id = ? OR sr.branch_id IS NULL)
  ORDER BY s.display_name COLLATE NOCASE`;

// Those of them who hold `permission` at the branch (for a manager-PIN approval).
const APPROVERS_SQL = `SELECT DISTINCT s.id, s.display_name FROM staff s
  JOIN staff_role sr ON sr.staff_id = s.id
  JOIN role r ON r.id = sr.role_id
  JOIN role_permission rp ON rp.role_id = sr.role_id
  WHERE s.active = 1 AND (sr.branch_id = ? OR sr.branch_id IS NULL) AND rp.permission_key = ?
  ORDER BY s.display_name COLLATE NOCASE`;

export class StaffSignIn {
  readonly #engine: SyncEngine;
  readonly #now: () => Date;
  readonly #policy: PinLockoutPolicy;
  /**
   * One PIN check at a time (review CR8B-24): each reads the lockout, runs scrypt, then writes
   * it; two at once would each count from the same state and lose a failure.
   */
  readonly #pinChecks = new Mutex();

  constructor(options: StaffSignInOptions) {
    this.#engine = options.engine;
    this.#now = options.now ?? (() => new Date());
    this.#policy = options.policy ?? DEFAULT_PIN_LOCKOUT_POLICY;
  }

  /** Active staff who hold a role for this branch (or for all branches), by name. */
  async listStaff(branchId: string): Promise<StaffMember[]> {
    const rows = await this.#engine.getAll<{ id: string; display_name: string }>(
      STAFF_AT_BRANCH_SQL,
      [branchId],
    );
    return rows.map((r) => ({ id: r.id, displayName: r.display_name }));
  }

  /** Active staff who may approve `permission` at this branch, by name. */
  async listApprovers(branchId: string, permission: PermissionKey): Promise<StaffMember[]> {
    const rows = await this.#engine.getAll<{ id: string; display_name: string }>(APPROVERS_SQL, [
      branchId,
      permission,
    ]);
    return rows.map((r) => ({ id: r.id, displayName: r.display_name }));
  }

  /** What the PIN pad shows now. */
  async lockout(): Promise<PinLockoutStatus> {
    return pinLockoutStatus(await this.#lockoutState(), this.#now(), this.#policy);
  }

  /** Checks a PIN (async: scrypt). Every failure counts toward the device lock. */
  async signIn(staffId: string, pin: string, branchId: string): Promise<SignInResult> {
    const checked = await this.#checkPin(staffId, pin, branchId);
    if (!checked.ok) return checked;
    return {
      ok: true,
      session: {
        staff: checked.staff,
        branchId,
        grants: await this.grantsFor(staffId),
        signedInAt: checked.at.toISOString(),
      },
    };
  }

  /**
   * A manager's PIN approving one action (D-048): the PIN must be right (wrong ones count
   * toward the device lock) and the person must hold `permission` at this branch.
   */
  async approve(
    staffId: string,
    pin: string,
    branchId: string,
    permission: PermissionKey,
  ): Promise<ApprovalResult> {
    const checked = await this.#checkPin(staffId, pin, branchId);
    if (!checked.ok) return checked;
    if (!hasPermission(await this.grantsFor(staffId), permission, branchId)) {
      return { ok: false, reason: 'not_permitted' };
    }
    return { ok: true, approver: checked.staff, approvedAt: checked.at.toISOString() };
  }

  /** The staff member's permission grants, from the synced roles that still exist. */
  async grantsFor(staffId: string): Promise<PermissionGrant[]> {
    const rows = await this.#engine.getAll<{ branch_id: string | null; permission_key: string }>(
      `SELECT sr.branch_id, rp.permission_key FROM staff_role sr
         JOIN role r ON r.id = sr.role_id
         JOIN role_permission rp ON rp.role_id = sr.role_id
        WHERE sr.staff_id = ?`,
      [staffId],
    );
    const byBranch = new Map<string | null, PermissionKey[]>();
    for (const r of rows) {
      if (!isPermissionKey(r.permission_key)) continue;
      const list = byBranch.get(r.branch_id) ?? [];
      list.push(r.permission_key);
      byBranch.set(r.branch_id, list);
    }
    return grantsFromAssignments(
      [...byBranch].map(([branchId, permissions]) => ({ branchId, permissions })),
    );
  }

  #checkPin(
    staffId: string,
    pin: string,
    branchId: string,
  ): Promise<{ ok: true; staff: StaffMember; at: Date } | PinFailure> {
    return this.#pinChecks.run(() => this.#checkPinNow(staffId, pin, branchId));
  }

  async #checkPinNow(
    staffId: string,
    pin: string,
    branchId: string,
  ): Promise<{ ok: true; staff: StaffMember; at: Date } | PinFailure> {
    const state = await this.#lockoutState();
    const status = pinLockoutStatus(state, this.#now(), this.#policy);
    if (status.locked) return { ok: false, reason: 'locked', retryAt: status.retryAt ?? '' };

    const staff = (await this.listStaff(branchId)).find((s) => s.id === staffId);
    if (!staff) return { ok: false, reason: 'unknown_staff' };
    const row = await this.#engine.getOptional<{ pin_hash: string | null }>(
      'SELECT pin_hash FROM staff WHERE id = ?',
      [staffId],
    );
    if (!row?.pin_hash) return { ok: false, reason: 'pin_unusable' };

    let ok: boolean;
    try {
      ok = await verifyPin(pin, row.pin_hash);
    } catch {
      // A malformed hash is corrupt data, not a wrong PIN: it doesn't count as an attempt.
      return { ok: false, reason: 'pin_unusable' };
    }
    const now = this.#now();
    if (!ok) {
      const next = recordPinFailure(state, now, this.#policy);
      await this.#saveLockout(next, now);
      const after = pinLockoutStatus(next, now, this.#policy);
      return {
        ok: false,
        reason: 'wrong_pin',
        attemptsLeft: after.attemptsLeft,
        lockedUntil: after.retryAt,
        nextLockMs: lockLengthMs(next, now, this.#policy),
      };
    }
    await this.#saveLockout(recordPinSuccess(state), now);
    return { ok: true, staff, at: now };
  }

  async #lockoutState(): Promise<PinLockoutState> {
    const state = (await readDeviceState<PinLockoutState>(
      this.#engine,
      DEVICE_STATE_KEYS.pinLockout,
    )) ?? { ...INITIAL_PIN_LOCKOUT };
    // Only this station's devices sync (stream `station_devices`): the newest unlock of any.
    const unlock = await this.#engine.getOptional<{ at: string | null }>(
      'SELECT MAX(pin_unlock_requested_at) AS at FROM device',
    );
    const at = unlock?.at ?? null;
    if (at === null) return state;
    const seen = await readDeviceState<string>(this.#engine, DEVICE_STATE_KEYS.pinUnlockSeen);
    if (seen !== null && instantMs(at) <= instantMs(seen)) return state;
    const now = this.#now();
    await this.#engine.writeTransaction(async (tx) => {
      await writeDeviceState(tx, DEVICE_STATE_KEYS.pinLockout, INITIAL_PIN_LOCKOUT, now);
      await writeDeviceState(tx, DEVICE_STATE_KEYS.pinUnlockSeen, at, now);
    });
    return { ...INITIAL_PIN_LOCKOUT };
  }

  async #saveLockout(state: PinLockoutState, now: Date) {
    await this.#engine.writeTransaction((tx) =>
      writeDeviceState(tx, DEVICE_STATE_KEYS.pinLockout, state, now),
    );
  }
}

/** An instant as synced (`2026-09-30 08:00:00Z` or ISO) in milliseconds; NaN sorts first. */
function instantMs(value: string): number {
  const ms = Date.parse(value.includes('T') ? value : value.replace(' ', 'T'));
  return Number.isNaN(ms) ? -Infinity : ms;
}

/**
 * How long the next lock lasts, from a lockout state (the domain rule: the base, doubled for
 * every earlier lock, capped).
 */
export function lockLengthMs(
  state: PinLockoutState,
  now: Date,
  policy: PinLockoutPolicy = DEFAULT_PIN_LOCKOUT_POLICY,
): number {
  // While locked, `lockouts` already counts the lock that is running: report that one.
  const locked = pinLockoutStatus(state, now, policy).locked;
  const earlier = Math.max(0, state.lockouts - (locked ? 1 : 0));
  return Math.min(policy.baseLockMs * 2 ** earlier, policy.maxLockMs);
}

/** Whether a signed-in staff member may do `key` at this branch. */
export function sessionCan(session: StaffSession | null, key: PermissionKey): boolean {
  return session !== null && hasPermission(session.grants, key, session.branchId);
}
